"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlanChangeDialog } from "@/components/ui/plan-change-dialog";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useAuth } from "@clerk/clerk-react";
import { useLanguageNavigate } from "@/lib/hooks/useLanguageNavigate";
import { getLanguageFromPath, addLanguageToPath } from "@/lib/language";
import { useCheckout, useSubscription } from "@/lib/hooks";
import { type PriceTier } from "@/api/stripe.service";
import { useTranslation } from "react-i18next";
import { usePlans } from "@/lib/hooks/usePlans";
import { type Plan } from "@/api/subscription.service";
import { trackSubscription } from "@/lib/analytics";
import { useCurrency, type Currency } from "@/lib/hooks/useCurrency";

interface PricingCardProps extends React.HTMLAttributes<HTMLDivElement> {
  tier: "S" | "M" | "L";
  tierDesc: string;
  title: string;
  description: string;
  price: number;
  currency: Currency;
  priceSubtext?: string;
  features: string[];
  ctaText?: string;
  priceId?: string;
  priceTier: PriceTier;
  onCheckout?: (priceId: string, tier: PriceTier) => void;
  isLoading?: boolean;
  isCurrentPlan?: boolean;
}

function PricingCard({
  tier,
  tierDesc,
  title,
  description,
  price,
  currency,
  features,
  ctaText,
  priceId,
  priceTier,
  onCheckout,
  isLoading = false,
  isCurrentPlan = false,
  className,
  ...props
}: PricingCardProps) {
  const { t } = useTranslation();
  const defaultCtaText = ctaText ?? t("pricing.order");

  const handleClick = () => {
    // Track plan view
    const planName = tier === 'S' ? 'Small' : tier === 'M' ? 'Medium' : 'Large';
    trackSubscription.planView(planName);

    if (onCheckout && !isLoading && !isCurrentPlan && priceId) {
      onCheckout(priceId, priceTier);
    }
  };

  return (
    <Card
      onClick={handleClick}
      className={cn(
        "relative flex flex-col border-0 overflow-hidden rounded-[5px] my-4 p-8 cursor-pointer",
        "hover:ring-2 hover:ring-black hover:shadow-2xl hover:scale-105 transition-all duration-300 ease-in-out",
        "2k:p-16 2k:rounded-lg 2k:hover:ring-4",
        "4k:p-24 4k:rounded-xl 4k:hover:ring-4",
        tier === "M" && "bg-cream",
        tier === "L" && "bg-rose-muted",
        className
      )}
      {...props}
    >
      {/* Tier Badge */}
      <CardHeader className="flex flex-row justify-between p-0">
        <p className={cn(
          "text-[220px] font-bold leading-40 translate-y-2",
          "2k:text-[320px] 2k:leading-60 2k:mb-4",
          "4k:text-[440px] 4k:leading-90 4k:mb-6"
        )}>
          {tier}
        </p>
        <p className={cn(
          "text-[14px]",
          "2k:text-[20px]",
          "4k:text-[28px]"
        )}>
          {tierDesc}
        </p>
      </CardHeader>

      <CardContent className={cn(
        "flex-1 space-y-4 p-0",
        "2k:space-y-8 2k:mb-4",
        "4k:space-y-12 4k:mb-6"
      )}>
        <p className="font-body">{description}</p>
        <p className="font-h3">{title}</p>
        <div className={cn(
          "flex w-fit items-center gap-1 bg-lavender-dark px-3 rounded-[5px]",
          "2k:rounded-sm 2k:px-6 2k:py-3",
          "4k:rounded-md 4k:px-10 4k:py-4"
        )}>
          <span className={cn(
            "font-h3",
            "2k:text-[32px]",
            "4k:text-[44px]"
          )}>
            {currency === "eur" ? `€${price}` : `${price} kr`}
          </span>
          <span className={cn(
            "text-[26px]",
            "2k:text-[32px]",
            "4k:text-[52px]"
          )}>{t("pricing.perMonth")}</span>
        </div>
      </CardContent>

      <CardFooter className="p-0">
        <Button
          variant={"default"}
          onClick={handleClick}
          disabled={isLoading || isCurrentPlan}
          className={cn(
            "rounded-sm font-link cursor-pointer w-full p-8",
            "2k:p-12 2k:rounded-md",
            "4k:p-18 4k:rounded-xl",
            isCurrentPlan 
              ? "bg-gray-400 cursor-default text-black" 
              : "bg-black hover:bg-black/90 text-white",
            isLoading && "opacity-50 cursor-not-allowed"
          )}
        >
          {isLoading ? t("pricing.loading") : isCurrentPlan ? t("pricing.currentPlan") : defaultCtaText}
        </Button>
      </CardFooter>
    </Card>
  );
}

interface PricingSectionProps extends React.HTMLAttributes<HTMLElement> {}

interface PricingPlan extends Omit<PricingCardProps, "onCheckout" | "isLoading"> {
  videosPerMonth: number;
  sekPriceId?: string;  // SEK Stripe Price ID (fallback)
  eurPriceId?: string;  // EUR Stripe Price ID
}

export function usePricingPlans(currency: Currency): PricingPlan[] {
  const { t } = useTranslation();
  const { data } = usePlans();
  
  return useMemo(() => {
    if (!data?.plans) return [];

    return data.plans.map((plan: Plan) => {
      // Determine tier based on name (Small/Medium/Large)
      const nameLower = plan.name.toLowerCase();
      let tierLiteral: "S" | "M" | "L" = "S";
      let priceTier: PriceTier = "small";

      if (nameLower.includes("medium")) {
        tierLiteral = "M";
        priceTier = "medium";
      } else if (nameLower.includes("large")) {
        tierLiteral = "L";
        priceTier = "large";
      }

      // Determine which price + stripePriceId to use based on detected currency
      const useEur = currency === "eur" && plan.eurPrice != null && plan.eurStripePriceId != null;
      const displayPrice = useEur
        ? (plan.eurPrice! / 100)
        : (plan.price / 100);
      const activePriceId = useEur ? plan.eurStripePriceId! : plan.stripePriceId;

      return {
        tier: tierLiteral,
        tierDesc: t(`pricing.plans.${priceTier}.tierDesc`),
        title: t(`pricing.plans.${priceTier}.title`, { count: plan.videosPerMonth }),
        description: plan.description || t(`pricing.plans.${priceTier}.description`),
        price: displayPrice,
        currency,
        features: [t(`pricing.plans.${priceTier}.title`, { count: plan.videosPerMonth })],
        ctaText: t("pricing.order"),
        priceId: activePriceId,
        priceTier: priceTier,
        videosPerMonth: plan.videosPerMonth,
        sekPriceId: plan.stripePriceId,
        eurPriceId: plan.eurStripePriceId || undefined,
      };
    }).sort((a: PricingPlan, b: PricingPlan) => a.price - b.price);
  }, [data, t, currency]);
}

function PricingSection({ className, ...props }: PricingSectionProps) {
  const titleRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const [loadingTier, setLoadingTier] = useState<PriceTier | null>(null);
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const plans = usePricingPlans(currency);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState<{
    priceId: string;
    tier: PriceTier;
  } | null>(null);
  
  const { isSignedIn } = useAuth();
  const navigate = useLanguageNavigate();
  const location = useLocation();
  const checkoutMutation = useCheckout();
  const { data: subscription, isLoading: isSubscriptionLoading } = useSubscription();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  // Map subscription plan name to PriceTier
  const getCurrentTier = (): PriceTier | null => {
    if (!subscription?.hasSubscription) return null;
    const planName = subscription.planName?.toLowerCase();
    if (planName === 'small') return 'small';
    if (planName === 'medium') return 'medium';
    if (planName === 'large') return 'large';
    return null;
  };

  const currentTier = getCurrentTier();

  // Determine if plan change is upgrade or downgrade
  const getChangeType = (targetTier: PriceTier): "upgrade" | "downgrade" | null => {
    if (!currentTier) return null;
    
    const currentPlan = plans.find(p => p.priceTier === currentTier);
    const targetPlan = plans.find(p => p.priceTier === targetTier);
    
    if (!currentPlan || !targetPlan) return null;
    
    return targetPlan.videosPerMonth > currentPlan.videosPerMonth ? "upgrade" : "downgrade";
  };

  const handleCheckout = async (priceId: string, tier: PriceTier) => {
    // If not signed in, redirect to sign-in with return URL
    if (!isSignedIn) {
      // Get current language and construct language-aware redirect URL
      const currentLanguage = getLanguageFromPath(location.pathname);
      const redirectPath = addLanguageToPath('/?section=pricing', currentLanguage);
      navigate(`/sign-in?redirect_url=${encodeURIComponent(redirectPath)}`);
      return;
    }

    // If already subscribed to this tier, don't proceed
    if (currentTier === tier) {
      return;
    }

    // If user has an active subscription, show confirmation dialog
    if (currentTier) {
      setPendingChange({ priceId, tier });
      setDialogOpen(true);
      return;
    }

    // No current subscription, proceed directly to checkout
    await proceedWithCheckout(priceId, tier);
  };

  const proceedWithCheckout = async (priceId: string, tier: PriceTier) => {
    // Track subscription upgrade attempt
    const planName = tier === 'small' ? 'Small' : tier === 'medium' ? 'Medium' : 'Large';
    trackSubscription.upgrade(planName);

    setLoadingTier(tier);
    try {
      await checkoutMutation.mutateAsync({ priceId });
    } catch (error) {
      console.error("Checkout failed:", error);
      setLoadingTier(null);
    }
  };

  const handleConfirmChange = async () => {
    if (!pendingChange) return;
    setDialogOpen(false);
    await proceedWithCheckout(pendingChange.priceId, pendingChange.tier);
    // Wait for exit animation to complete before clearing state
    setTimeout(() => {
      setPendingChange(null);
    }, 500);
  };

  const handleCancelDialog = () => {
    setDialogOpen(false);
    // Wait for exit animation to complete before unmounting
    setTimeout(() => {
      setPendingChange(null);
    }, 500);
  };
  
  useEffect(() => {
    const check = () => setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useGSAP(() => {
    // Only animate if data is loaded
    if (plans.length === 0) return;

    const ctx = gsap.context( async () => {
      if(titleRef.current){
        gsap.set(titleRef.current, {opacity: 0})
        gsap.fromTo(titleRef.current, {opacity: 0}, {
          opacity: 1,
          stagger: 0.15,
          scrollTrigger: {
            trigger: titleRef.current,
            start: "top center",
            end: "top 30%",
            scrub: 1,
          }
        });          
      }

      if(cardsRef.current){
        const cardsChildren = Array.from(cardsRef.current.children);
        cardsChildren.forEach((child) => {
          gsap.set(
            child, 
            { opacity: 0, y: 60 }
          );
        });
        gsap.fromTo(
          cardsChildren,
          { opacity: 0, y: 60 },
          { 
            opacity: 1, 
            y: 0,
            stagger: 0.15,
            scrollTrigger: {
              trigger: titleRef.current,
              start: "top center",
              end: "center center",
              scrub: 1,
            },
          },
        );
      }
    });

    return () => ctx.revert();
  }, [plans.length]); // Re-run animation when plans loaded

  return (
    <section
      id="pricing"
      className={cn(
        "px-6 pt-12",
        "4k:pt-28",
        className
      )}
      {...props}
    >
      <div ref={titleRef} className={cn(
        "flex flex-col gap-4 mb-6",
        "md:flex-row md:items-center md:mb-12 md:gap-20",
        "4k:mb-30 4k:gap-40",
      )}>
        <p className="font-h1">{t("pricing.title")}</p>
        <p className="font-paragraph">
          {t("pricing.subtitle").split('\n').map((line, i) => (
            <span key={i}>{line}{i === 0 && <br />}</span>
          ))}
        </p>
      </div>
      
      <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 md:gap-6 4k:gap-12">
        {plans.map((plan) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { videosPerMonth, ...pricingCardProps } = plan;
          return (
            <div key={plan.tier} className="flex h-full">
              <PricingCard 
                {...pricingCardProps} 
                onCheckout={handleCheckout}
                isLoading={loadingTier === plan.priceTier}
                isCurrentPlan={currentTier === plan.priceTier}
              />
            </div>
          );
        })}
      </div>

      {/* Enterprise / custom quote banner */}
      <div className={cn(
        "mt-8 mb-10 md:mb-0 md:px-8 text-center",
        "2k:mt-12 2k:px-16 2k:py-10 2k:rounded-lg",
        "4k:mt-16 4k:px-24 4k:py-14 4k:rounded-xl"
      )}>
        <p className={cn(
          isMobile ? "text-[24px] font-bold leading-7" : "font-paragraph",
          "mb-3 2k:mb-2 4k:mb-3"
        )}>
          {t("pricing.enterpriseTitle")}
        </p>
        <p className={cn(
          "text-[19px]",
          "md:text-[24px]",
          "2k:text-[30px]",
          "4k:text-[46px]"
        )}>
          {t("pricing.enterpriseDesc", { email: "info@dovie.co" }).split("info@dovie.co").map((part, i, arr) =>
            i < arr.length - 1 ? (
              <span key={i}>
                {part}
                <a href="mailto:info@dovie.co" className="font-bold underline">
                  info@dovie.co
                </a>
              </span>
            ) : (
              <span key={i}>{part}</span>
            )
          )}
        </p>
      </div>

      {/* Plan Change Confirmation Dialog */}
      {pendingChange && currentTier && (
        <PlanChangeDialog
          isOpen={dialogOpen}
          onClose={handleCancelDialog}
          onConfirm={handleConfirmChange}
          isLoading={loadingTier === pendingChange.tier}
          changeType={getChangeType(pendingChange.tier) || "upgrade"}
          currentPlan={plans.find(p => p.priceTier === currentTier)?.title || ""}
          newPlan={plans.find(p => p.priceTier === pendingChange.tier)?.title || ""}
          currentPlanVideos={plans.find(p => p.priceTier === currentTier)?.videosPerMonth || 0}
          newPlanVideos={plans.find(p => p.priceTier === pendingChange.tier)?.videosPerMonth || 0}
        />
      )}
    </section>
  );
}

export { PricingCard, PricingSection };
